import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  doc, 
  arrayUnion, 
  arrayRemove, 
  serverTimestamp, 
  increment 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../../../services/firebase/firebase';

/**
 * Social Service
 * 
 * This service provides functions for managing social posts and comments.
 */
const socialService = {
  /**
   * Create a new post
   * 
   * @param {Object} postData - Post data to create
   * @param {Object} mediaFile - Media file to upload (optional)
   * @param {string} mediaType - Type of media (image/video)
   * @param {Function} onProgress - Progress callback for media upload
   * @returns {Promise<string>} - Created post ID
   */
  createPost: async (postData, mediaFile = null, mediaType = null, onProgress = () => {}) => {
    try {
      let mediaData = null;

      // Upload media if exists
      if (mediaFile) {
        const fileName = `${postData.authorId}_${Date.now()}-${mediaType}`;
        const storageRef = ref(storage, `posts/${postData.authorId}/${fileName}`);
        
        const uploadTask = uploadBytesResumable(storageRef, mediaFile);
        
        // Set up upload progress tracking
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              onProgress(progress);
            },
            (error) => {
              console.error('Upload error:', error);
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              mediaData = {
                type: mediaType,
                url: downloadURL
              };
              resolve();
            }
          );
        });
      }

      // Add media if available
      if (mediaData) {
        postData.media = mediaData;
      }

      // Add the post to Firestore
      const postRef = collection(db, 'posts');
      const postDoc = await addDoc(postRef, {
        ...postData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        likes: [],
        likesCount: 0,
        commentsCount: 0
      });

      return postDoc.id;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  },

  /**
   * Get posts for feed
   * 
   * @param {number} postsCount - Number of posts to fetch
   * @param {Object} lastPost - Last post document for pagination
   * @returns {Promise<Object>} - Posts and pagination info
   */
  getFeedPosts: async (postsCount = 10, lastPost = null) => {
    try {
      let postsQuery;
      
      if (lastPost) {
        postsQuery = query(
          collection(db, 'posts'), 
          orderBy('createdAt', 'desc'), 
          startAfter(lastPost), 
          limit(postsCount)
        );
      } else {
        postsQuery = query(
          collection(db, 'posts'), 
          orderBy('createdAt', 'desc'), 
          limit(postsCount)
        );
      }
      
      const snapshot = await getDocs(postsQuery);
      
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const lastVisible = snapshot.docs.length > 0 
        ? snapshot.docs[snapshot.docs.length - 1]
        : null;
      
      const hasMore = snapshot.docs.length === postsCount;
      
      return { posts, lastVisible, hasMore };
    } catch (error) {
      console.error('Error fetching feed posts:', error);
      throw error;
    }
  },

  /**
   * Get posts by a specific user
   * 
   * @param {string} userId - User ID to fetch posts for
   * @param {number} postsCount - Number of posts to fetch
   * @param {Object} lastPost - Last post document for pagination
   * @returns {Promise<Object>} - Posts and pagination info
   */
  getUserPosts: async (userId, postsCount = 10, lastPost = null) => {
    try {
      let postsQuery;
      
      if (lastPost) {
        postsQuery = query(
          collection(db, 'posts'),
          where('authorId', '==', userId),
          orderBy('createdAt', 'desc'),
          startAfter(lastPost),
          limit(postsCount)
        );
      } else {
        postsQuery = query(
          collection(db, 'posts'),
          where('authorId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(postsCount)
        );
      }
      
      const snapshot = await getDocs(postsQuery);
      
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const lastVisible = snapshot.docs.length > 0 
        ? snapshot.docs[snapshot.docs.length - 1]
        : null;
      
      const hasMore = snapshot.docs.length === postsCount;
      
      return { posts, lastVisible, hasMore };
    } catch (error) {
      console.error('Error fetching user posts:', error);
      throw error;
    }
  },

  /**
   * Get a single post by ID
   * 
   * @param {string} postId - ID of the post to fetch
   * @returns {Promise<Object|null>} - Post data or null if not found
   */
  getPostById: async (postId) => {
    try {
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      
      if (postDoc.exists()) {
        return {
          id: postDoc.id,
          ...postDoc.data()
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      throw error;
    }
  },

  /**
   * Update a post
   * 
   * @param {string} postId - ID of the post to update
   * @param {Object} postData - Updated post data
   * @returns {Promise<void>}
   */
  updatePost: async (postId, postData) => {
    try {
      const postRef = doc(db, 'posts', postId);
      
      await updateDoc(postRef, {
        ...postData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  },

  /**
   * Delete a post
   * 
   * @param {string} postId - ID of the post to delete
   * @param {Object} postData - Post data for media cleanup
   * @returns {Promise<void>}
   */
  deletePost: async (postId, postData) => {
    try {
      // Delete media file if exists
      if (postData.media && postData.media.url) {
        // Extract storage path from the URL
        const url = new URL(postData.media.url);
        const pathWithQueryString = url.pathname;
        const path = pathWithQueryString.split('?')[0]; // Remove query parameters
        const storagePath = path.replace('/v0/b/wingmancoza.firebasestorage.app/o/', '');
        const decodedPath = decodeURIComponent(storagePath);
        
        try {
          const mediaRef = ref(storage, decodedPath);
          await deleteObject(mediaRef);
        } catch (mediaError) {
          console.error('Error deleting media file:', mediaError);
          // Continue with post deletion even if media delete fails
        }
      }
      
      // Delete post comments
      const commentsRef = collection(db, `posts/${postId}/comments`);
      const commentsSnapshot = await getDocs(commentsRef);
      
      const deletePromises = commentsSnapshot.docs.map(commentDoc => 
        deleteDoc(doc(db, `posts/${postId}/comments`, commentDoc.id))
      );
      
      await Promise.all(deletePromises);
      
      // Delete the post document
      await deleteDoc(doc(db, 'posts', postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  },

  /**
   * Like or unlike a post
   * 
   * @param {string} postId - ID of the post
   * @param {string} userId - ID of the user liking/unliking
   * @param {boolean} isLiked - Whether the post is already liked
   * @returns {Promise<void>}
   */
  toggleLikePost: async (postId, userId, isLiked) => {
    try {
      const postRef = doc(db, 'posts', postId);
      
      await updateDoc(postRef, {
        likes: isLiked ? arrayRemove(userId) : arrayUnion(userId),
        likesCount: isLiked ? increment(-1) : increment(1)
      });
    } catch (error) {
      console.error('Error toggling post like:', error);
      throw error;
    }
  },

  /**
   * Get comments for a post
   * 
   * @param {string} postId - ID of the post
   * @returns {Promise<Array>} - Array of comments
   */
  getPostComments: async (postId) => {
    try {
      const commentsRef = collection(db, `posts/${postId}/comments`);
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  },

  /**
   * Add a comment to a post
   * 
   * @param {string} postId - ID of the post
   * @param {Object} commentData - Comment data
   * @returns {Promise<string>} - ID of the created comment
   */
  addComment: async (postId, commentData) => {
    try {
      const commentsRef = collection(db, `posts/${postId}/comments`);
      
      const commentDoc = await addDoc(commentsRef, {
        ...commentData,
        createdAt: serverTimestamp(),
        likes: [],
        likesCount: 0
      });
      
      // Increment post's comment count
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        commentsCount: increment(1)
      });
      
      return commentDoc.id;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },

  /**
   * Add a reply to a comment
   * 
   * @param {string} postId - ID of the post
   * @param {string} parentCommentId - ID of the parent comment
   * @param {Object} replyData - Reply data
   * @returns {Promise<string>} - ID of the created reply
   */
  addReply: async (postId, parentCommentId, replyData) => {
    try {
      // Add reply as a comment with parentCommentId
      const commentsRef = collection(db, `posts/${postId}/comments`);
      
      const replyDoc = await addDoc(commentsRef, {
        ...replyData,
        parentCommentId,
        createdAt: serverTimestamp(),
        likes: [],
        likesCount: 0
      });
      
      // Update parent comment's reply count
      const commentRef = doc(db, `posts/${postId}/comments`, parentCommentId);
      await updateDoc(commentRef, {
        repliesCount: increment(1)
      });
      
      return replyDoc.id;
    } catch (error) {
      console.error('Error adding reply:', error);
      throw error;
    }
  },

  /**
   * Delete a comment or reply
   * 
   * @param {string} postId - ID of the post
   * @param {string} commentId - ID of the comment to delete
   * @param {boolean} isReply - Whether this is a reply
   * @param {string} parentCommentId - ID of parent comment if it's a reply
   * @returns {Promise<void>}
   */
  deleteComment: async (postId, commentId, isReply = false, parentCommentId = null) => {
    try {
      // Delete the comment document
      await deleteDoc(doc(db, `posts/${postId}/comments`, commentId));
      
      // If it's a main comment, decrement post comment count
      if (!isReply) {
        const postRef = doc(db, 'posts', postId);
        await updateDoc(postRef, {
          commentsCount: increment(-1)
        });
      } 
      // If it's a reply, decrement parent comment reply count
      else if (parentCommentId) {
        const commentRef = doc(db, `posts/${postId}/comments`, parentCommentId);
        await updateDoc(commentRef, {
          repliesCount: increment(-1)
        });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  },

  /**
   * Like or unlike a comment/reply
   * 
   * @param {string} postId - ID of the post
   * @param {string} commentId - ID of the comment/reply
   * @param {string} userId - ID of the user liking/unliking
   * @param {boolean} isLiked - Whether already liked
   * @returns {Promise<void>}
   */
  toggleLikeComment: async (postId, commentId, userId, isLiked) => {
    try {
      const commentRef = doc(db, `posts/${postId}/comments`, commentId);
      
      await updateDoc(commentRef, {
        likes: isLiked ? arrayRemove(userId) : arrayUnion(userId),
        likesCount: isLiked ? increment(-1) : increment(1)
      });
    } catch (error) {
      console.error('Error toggling comment like:', error);
      throw error;
    }
  },

  /**
   * Format timestamp for display
   * 
   * @param {Object} timestamp - Firestore timestamp
   * @returns {string} - Formatted time string
   */
  formatTimestamp: (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const postDate = timestamp.toDate();
    const diffInSeconds = Math.floor((now - postDate) / 1000);
    
    // Less than a minute
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    
    // Less than an hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    // Less than a day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    // Less than a week
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
    
    // Default: show the date
    return postDate.toLocaleDateString();
  }
};

export default socialService;